import React from 'react';
import { Box, Typography, Card, CardContent, Chip } from '@mui/material';
import {
  QuestionAnswer as QuestionAnswerIcon,
  PersonOutlineOutlined,
  ScheduleOutlined,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

interface RawDataSectionProps {
  responses: Array<{
    responseId: string;
    value: string | number;
    timestamp: string;
  }>;
  maxResponses?: number;
  showEmptyState?: boolean;
  compact?: boolean;
  colorMapping?: Record<string, string>;
  className?: string;
}

interface ResponseItemProps {
  response: {
    responseId: string;
    value: string | number;
    timestamp: string;
  };
  compact?: boolean;
  colorCode?: string;
}

const ResponseItem: React.FC<ResponseItemProps> = ({ response, compact = false, colorCode }) => {
  const formatTimestamp = (timestamp: string): string => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: compact ? 1 : 2,
        backgroundColor: 'transparent',
        border: '2px solid',
        borderColor: colorCode || 'divider',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <CardContent sx={{ p: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
        {/* Response value and metadata */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              flex: 1,
            }}
          >
            {response.value}
          </Typography>

          <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <ScheduleOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {formatTimestamp(response.timestamp)}
              </Typography>
            </Box>

            <Chip
              label={response.responseId}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: 'transparent',
                borderColor: colorCode || 'primary.main',
                color: colorCode || 'primary.main',
                fontWeight: 'bold',
              }}
              icon={<PersonOutlineOutlined sx={{ fontSize: 16 }} />}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const RawDataSection: React.FC<RawDataSectionProps> = ({
  responses,
  maxResponses,
  showEmptyState = true,
  compact = false,
  colorMapping,
  className,
}) => {
  // Determine which responses to show
  const responsesToShow = maxResponses ? responses.slice(0, maxResponses) : responses;

  if (responses.length === 0) {
    if (!showEmptyState) return null;

    return (
      <Box className={className} textAlign="center" py={4}>
        <QuestionAnswerIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          No responses available for this question.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={className}>
      {/* Responses list */}
      <Box>
        {responsesToShow.map((response, index) => (
          <ResponseItem
            key={`${response.responseId}-${index}`}
            response={response}
            compact={compact}
            colorCode={colorMapping?.[response.responseId]}
          />
        ))}

        {/* Show more indicator */}
        {maxResponses && responses.length > maxResponses && (
          <Box textAlign="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              ... and {responses.length - maxResponses} more responses
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
